<template>
  <div class="edit-repair-order">
    <el-form
      ref="form"
      :model="formData"
      :rules="rules"
      label-width="120px"
      v-loading="loading"
    >
      <!-- 报修类型（单独一行） -->
      <el-form-item label="报修类型" prop="orderType">
        <el-radio-group v-model="formData.orderType" @change="handleOrderTypeChange">
          <el-radio
            v-for="item in repairScopes"
            :key="item.id"
            :label="item.templateCode"
          >
            {{ item.name }}
          </el-radio>
        </el-radio-group>
      </el-form-item>

      <!-- 室内报修：选择楼栋单元房号（一行排列） -->
      <template v-if="formData.orderType === 0">
        <el-row :gutter="20">
          <el-col :span="8">
            <el-form-item label="选择楼栋" prop="buildingId">
              <el-select
                v-model="formData.buildingId"
                placeholder="请选择楼栋"
                style="width:100%"
                @change="handleBuildingChange"
              >
                <el-option
                  v-for="item in buildings"
                  :key="item.id"
                  :label="item.floorNum"
                  :value="item.id"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="选择单元" prop="unitId">
              <el-select
                v-model="formData.unitId"
                placeholder="请选择单元"
                style="width:100%"
                :disabled="!formData.buildingId"
                @change="handleUnitChange"
              >
                <el-option
                  v-for="item in units"
                  :key="item.id"
                  :label="item.unitNum"
                  :value="item.id"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="选择房号" prop="roomId">
              <el-select
                v-model="formData.roomId"
                placeholder="请选择房号"
                style="width:100%"
                filterable
                :disabled="!formData.unitId"
              >
                <el-option
                  v-for="item in rooms"
                  :key="item.roomId"
                  :label="`${item.roomNum}${item.ownerName ? '(' + item.ownerName + ')' : ''}`"
                  :value="item.roomId"
                />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
      </template>

      <!-- 公区报修：详细位置 -->
      <el-form-item label="详细位置" prop="addressDetail" v-if="formData.orderType === 1">
        <el-input
          v-model="formData.addressDetail"
          placeholder="请输入详细位置描述"
          maxlength="200"
        />
      </el-form-item>

      <!-- 报修分类：报修部位选择 -->
      <el-form-item label="报修部位" prop="itemId">
        <el-input
          v-model="selectedItemName"
          placeholder="请选择报修部位"
          readonly
          style="width:100%"
          @click.native="showCategoryDialog = true"
        >
          <i slot="suffix" class="el-input__icon el-icon-arrow-down"></i>
        </el-input>
      </el-form-item>

      <!-- 所属专业（只读显示） -->
      <el-form-item label="所属专业" v-if="selectedProfessionName">
        <span class="profession-name">{{ selectedProfessionName }}</span>
      </el-form-item>

      <!-- 报修分类选择弹窗 -->
      <el-dialog
        title="选择报修部位"
        :visible.sync="showCategoryDialog"
        width="70%"
        append-to-body
        class="category-dialog"
      >
        <div v-loading="categoryLoading" class="category-content">
          <div v-if="categoryData.length === 0 && !categoryLoading" class="empty-category">
            暂无数据
          </div>
          <div v-else class="category-sections">
            <div
              v-for="(profession, pIndex) in categoryData"
              :key="pIndex"
              class="category-section"
            >
              <div class="category-name">{{ profession.professionName }}</div>
              <div class="tag-grid">
                <div
                  v-for="(item, iIndex) in profession.items"
                  :key="iIndex"
                  class="tag-item"
                  :class="{ 'tag-active': tempItemId === item.itemId }"
                  @click="selectCategory(profession, item)"
                >
                  {{ item.itemName }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div slot="footer">
          <el-button @click="closeCategoryDialog">取消</el-button>
          <el-button type="primary" @click="confirmCategory">确定</el-button>
        </div>
      </el-dialog>

      <!-- 报修人信息 -->
      <el-form-item label="报修人" prop="initiatorName">
        <el-input
          v-model="formData.initiatorName"
          placeholder="请输入报修人姓名"
          maxlength="30"
        />
      </el-form-item>

      <el-form-item label="联系电话" prop="initiatorPhone">
        <el-input
          v-model="formData.initiatorPhone"
          type="tel"
          placeholder="请输入手机号"
          maxlength="11"
        />
      </el-form-item>

      <!-- 报修时间（一行排列） -->
      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="预约日期" prop="bookingDate">
            <el-date-picker
              v-model="formData.bookingDate"
              type="date"
              placeholder="请选择预约日期"
              style="width:100%"
              :picker-options="datePickerOptions"
              value-format="yyyy-MM-dd"
            />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="报修时间" prop="bookingTime">
            <el-time-picker
              v-model="formData.bookingTime"
              placeholder="请选择报修时间"
              style="width:100%"
              value-format="HH:mm"
              format="HH:mm"
              :picker-options="timePickerOptions"
            />
          </el-form-item>
        </el-col>
      </el-row>

      <!-- 问题描述 -->
      <el-form-item label="问题描述" prop="problemDesc">
        <el-input
          v-model="formData.problemDesc"
          type="textarea"
          :rows="4"
          placeholder="请详细描述故障情况，以便维修人员准备工具和配件"
          maxlength="500"
          show-word-limit
        />
      </el-form-item>

      <!-- 图片上传 -->
      <el-form-item label="上传图片" class="upload-form-item">
        <el-upload
          action=""
          list-type="picture-card"
          :file-list="imageList"
          :on-preview="handlePictureCardPreview"
          :on-remove="handleRemove"
          :auto-upload="true"
          :http-request="uploadImage"
          :limit="9"
        >
          <i class="el-icon-plus"></i>
        </el-upload>
        <div class="upload-tip">最多上传9张图片</div>
      </el-form-item>

      <div class="form-footer">
        <el-button @click="handleCancel">取消</el-button>
        <el-button type="primary" :loading="submitting" @click="handleSubmit">
          保存修改
        </el-button>
      </div>
    </el-form>
  </div>
</template>

<script>
import { saveOrUpdateRepairOrder } from '@/api/work/repairPoolManageApi'
import { listRepairPools } from '@/api/work/repairPoolManageApi'
import { listRepairFlowTemplates } from '@/api/work/repairFlowTemplateApi'
import { listRepairCategories } from '@/api/work/skillApi'
import { queryFloorAndUnits } from '@/api/room/roomStructureApi'
import { queryRooms } from '@/api/room/roomApi'
import { getCommunityId } from '@/api/community/communityApi'
import { getUserId } from '@/api/user/userApi'
import { uploadImage as uploadOwnerImage } from '@/api/owner/ownerApi'

export default {
  name: 'EditRepairOrder',
  props: {
    repairId: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      loading: false,
      submitting: false,
      repairScopes: [],
      buildings: [],
      units: [],
      rooms: [],
      categoryData: [],
      categoryLoading: false,
      showCategoryDialog: false,
      selectedProfessionName: '',
      selectedItemName: '',
      tempProfessionId: null,
      tempProfessionName: '',
      tempItemId: null,
      tempItemName: '',
      imageList: [],
      datePickerOptions: {
        disabledDate(time) {
          const today = new Date()
          today.setHours(0, 0, 0, 0)
          return time.getTime() < today.getTime()
        }
      },
      timePickerOptions: {
        selectableRange: '08:00:00 - 22:00:00'
      },
      formData: {
        id: null,
        orderType: 0,
        templateId: null,
        buildingId: null,
        unitId: null,
        roomId: null,
        addressDetail: '',
        professionId: null,
        itemId: null,
        initiatorName: '',
        initiatorPhone: '',
        bookingDate: '',
        bookingTime: '',
        problemDesc: '',
        problemImgs: ''
      },
      rules: {
        orderType: [{ required: true, message: '请选择报修类型', trigger: 'change' }],
        buildingId: [
          {
            validator: (rule, value, callback) => {
              if (this.formData.orderType === 0 && !value) {
                callback(new Error('请选择楼栋'))
              } else {
                callback()
              }
            },
            trigger: 'change'
          }
        ],
        unitId: [
          {
            validator: (rule, value, callback) => {
              if (this.formData.orderType === 0 && !value) {
                callback(new Error('请选择单元'))
              } else {
                callback()
              }
            },
            trigger: 'change'
          }
        ],
        roomId: [
          {
            validator: (rule, value, callback) => {
              if (this.formData.orderType === 0 && !value) {
                callback(new Error('请选择房号'))
              } else {
                callback()
              }
            },
            trigger: 'change'
          }
        ],
        addressDetail: [
          {
            validator: (rule, value, callback) => {
              if (this.formData.orderType === 1 && !value) {
                callback(new Error('请填写详细位置'))
              } else {
                callback()
              }
            },
            trigger: 'blur'
          }
        ],
        itemId: [{ required: true, message: '请选择报修部位', trigger: 'change' }],
        initiatorName: [
          { required: true, message: '请填写报修人', trigger: 'blur' },
          { max: 30, message: '报修人长度不能超过30位', trigger: 'blur' }
        ],
        initiatorPhone: [
          { required: true, message: '请填写联系电话', trigger: 'blur' },
          { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号', trigger: 'blur' }
        ],
        bookingDate: [{ required: true, message: '请选择预约日期', trigger: 'change' }],
        bookingTime: [{ required: true, message: '请选择报修时间', trigger: 'change' }],
        problemDesc: [{ required: true, message: '请填写问题描述', trigger: 'blur' }]
      }
    }
  },
  created() {
    this.loadRepairScopes()
    this.loadBuildings()
    this.loadCategoryData()
    this.loadRepairDetail()
  },
  methods: {
    // 加载工单详情
    async loadRepairDetail() {
      try {
        this.loading = true
        
        const savedRelCd = localStorage.getItem('repairPoolManage_relCd') || '1'
        const userInfo = JSON.parse(localStorage.getItem('user') || '{}')
        const handlerKey = userInfo.pgId || userInfo.handlerKey || ''
        
        const res = await listRepairPools({
          communityId: getCommunityId(),
          initiatorType: savedRelCd,
          userId: getUserId(),
          handlerKey: handlerKey,
          id: this.repairId,
          tabType: '1',
          page: 1,
          row: 1
        })
        
        if (res.code === 0 && res.data && res.data.length > 0) {
          const detail = res.data[0]
          
          // 回显基本信息
          this.formData.id = detail.id
          this.formData.orderType = detail.orderType
          this.formData.templateId = detail.templateId
          this.formData.initiatorName = detail.initiatorName
          this.formData.initiatorPhone = detail.initiatorPhone
          this.formData.problemDesc = detail.problemDesc || ''
          
          // 回显位置信息
          if (detail.orderType == 0) {
            this.formData.roomId = detail.roomId
            this.formData.buildingId = detail.buildingId
            this.formData.unitId = detail.unitId
            
            // 加载单元和房号列表
            if (this.formData.buildingId) {
              await this.handleBuildingChange(this.formData.buildingId, true)
            }
            if (this.formData.unitId) {
              await this.handleUnitChange(this.formData.unitId, true)
            }
          } else {
            this.formData.addressDetail = detail.addressDetail || ''
          }
          
          // 回显分类信息
          this.formData.professionId = detail.professionId
          this.formData.itemId = detail.itemId
          
          // 从分类数据中找到对应的名称
          if (this.categoryData.length > 0) {
            const profession = this.categoryData.find(p => p.professionId === detail.professionId)
            if (profession) {
              this.selectedProfessionName = profession.professionName
              const item = profession.items.find(i => i.itemId === detail.itemId)
              if (item) {
                this.selectedItemName = item.itemName
              }
            }
          }
          
          // 回显报修时间
          if (detail.bookingTime) {
            const bookingParts = detail.bookingTime.split(' ')
            if (bookingParts.length >= 2) {
              this.formData.bookingDate = bookingParts[0]
              this.formData.bookingTime = bookingParts[1].substring(0, 5)
            }
          }
          
          // 回显图片
          if (detail.problemImgs) {
            const imgUrls = detail.problemImgs.split(',').filter(img => img.trim())
            this.imageList = imgUrls.map((url, index) => ({
              uid: `img-${index}`,
              name: `image-${index}`,
              url: url
            }))
          }
        } else {
          this.$message.warning('未找到工单信息')
        }
      } catch (error) {
        console.log('加载工单详情失败:', error)
        this.$message.error(error.message || '加载失败')
      } finally {
        this.loading = false
      }
    },
    
    // 加载报修类型列表
    async loadRepairScopes() {
      try {
        const res = await listRepairFlowTemplates({
          page: 1,
          row: 100,
          isEnable: '1'
        })
        this.repairScopes = (res.data || []).map(item => ({
          id: item.id,
          templateCode: item.templateCode,
          name: item.templateName
        }))
      } catch (error) {
        console.log('加载报修类型失败:', error)
      }
    },
    
    // 报修类型切换
    handleOrderTypeChange(value) {
      const selected = this.repairScopes.find(item => item.templateCode === value)
      if (selected) {
        this.formData.templateId = selected.id
      }
      this.formData.buildingId = null
      this.formData.unitId = null
      this.formData.roomId = null
      this.formData.addressDetail = ''
      this.units = []
      this.rooms = []
    },
    
    // 加载楼栋列表
    async loadBuildings() {
      try {
        const res = await queryFloorAndUnits()
        const arr = res.data || res || []
        const buildingMap = {}
        arr.forEach(item => {
          if (!buildingMap[item.floorId]) {
            buildingMap[item.floorId] = {
              id: item.floorId,
              floorNum: item.floorNum + '号楼',
              floorNumRaw: item.floorNum,
              units: []
            }
          }
          if (item.unitId) {
            const existUnit = buildingMap[item.floorId].units.find(u => u.id === item.unitId)
            if (!existUnit) {
              buildingMap[item.floorId].units.push({
                id: item.unitId,
                unitNum: item.unitNum + '单元'
              })
            }
          }
        })
        this.buildings = Object.values(buildingMap)
      } catch (error) {
        console.log('加载楼栋列表失败:', error)
      }
    },
    
    // 楼栋选择
    async handleBuildingChange(value, isInit = false) {
      const building = this.buildings.find(b => b.id === value)
      if (building) {
        this.units = building.units || []
      } else {
        this.units = []
      }
      if (!isInit) {
        this.formData.unitId = null
        this.formData.roomId = null
        this.rooms = []
      }
    },
    
    // 单元选择
    async handleUnitChange(value, isInit = false) {
      if (!isInit) {
        this.formData.roomId = null
        this.rooms = []
      }
      if (value) {
        await this.loadRooms()
      }
    },
    
    // 加载房号列表
    async loadRooms() {
      try {
        const res = await queryRooms({
          communityId: getCommunityId(),
          floorId: this.formData.buildingId,
          unitId: this.formData.unitId,
          roomType: '1010301',
          flag: 1,
          page: 1,
          row: 100
        })
        const arr = res.rooms || res.data || res || []
        this.rooms = arr.map(item => ({
          roomId: item.roomId,
          roomNum: item.roomNum || item.roomName,
          ownerName: item.ownerName || '',
          ownerId: item.ownerId || '',
          ownerPhone: item.link || item.ownerPhone || ''
        }))
      } catch (error) {
        console.log('加载房号列表失败:', error)
      }
    },
    
    // 加载报修分类数据
    async loadCategoryData() {
      try {
        this.categoryLoading = true
        const res = await listRepairCategories({})
        const data = res.data || []
        const professions = data.filter(item => item.categoryType === 1)
        this.categoryData = professions.map(item => ({
          professionId: item.id,
          professionName: item.name,
          items: (item.childrenCategories || []).map(child => ({
            itemId: child.id,
            itemName: child.name
          }))
        }))
      } catch (error) {
        console.log('加载报修分类失败:', error)
      } finally {
        this.categoryLoading = false
      }
    },
    
    // 选择分类（临时选择）
    selectCategory(profession, item) {
      this.tempProfessionId = profession.professionId
      this.tempProfessionName = profession.professionName
      this.tempItemId = item.itemId
      this.tempItemName = item.itemName
    },
    
    // 确认选择分类
    confirmCategory() {
      if (!this.tempItemId) {
        this.$message.warning('请选择报修部位')
        return
      }
      this.formData.professionId = this.tempProfessionId
      this.formData.itemId = this.tempItemId
      this.selectedProfessionName = this.tempProfessionName
      this.selectedItemName = this.tempItemName
      this.showCategoryDialog = false
    },
    
    // 关闭分类选择弹窗
    closeCategoryDialog() {
      this.tempProfessionId = this.formData.professionId
      this.tempProfessionName = this.selectedProfessionName
      this.tempItemId = this.formData.itemId
      this.tempItemName = this.selectedItemName
      this.showCategoryDialog = false
    },
    
    // 图片上传
    async uploadImage(param) {
      try {
        const res = await uploadOwnerImage({ file: param.file })
        const url = res && (res.url || (res.data && res.data.url))
        if (!url) {
          throw new Error('上传失败，未返回图片地址')
        }
        const file = param.file
        file.url = url
        const index = this.imageList.findIndex(item => item.uid === file.uid)
        if (index > -1) {
          this.$set(this.imageList, index, file)
        } else {
          this.imageList.push(file)
        }
        param.onSuccess(res)
      } catch (e) {
        console.log('上传图片失败:', e)
        this.$message.error(e.message || '上传图片失败')
        param.onError(e)
      }
    },
    
    // 图片预览
    handlePictureCardPreview(file) {
      if (file && file.url) {
        window.open(file.url, '_blank')
      }
    },
    
    // 图片移除
    handleRemove(file) {
      if (!file || !file.url) return
      const index = this.imageList.findIndex(img => img.url === file.url)
      if (index > -1) {
        this.imageList.splice(index, 1)
      }
    },
    
    // 取消
    handleCancel() {
      this.$emit('cancel')
    },
    
    // 提交修改
    async handleSubmit() {
      this.$refs.form.validate(async valid => {
        if (!valid) {
          return false
        }

        const bookingTime = this.formData.bookingDate + ' ' + this.formData.bookingTime + ':00'
        const bookingTimestamp = new Date(bookingTime.replace(/-/g, '/')).getTime()
        const now = Date.now()
        if (bookingTimestamp <= now - 30 * 60 * 1000) {
          this.$message.warning('报修时间不能早于当前时间')
          return false
        }

        this.submitting = true
        try {
          const params = {
            id: this.formData.id,
            orderType: this.formData.orderType,
            templateId: this.formData.templateId,
            professionId: this.formData.professionId,
            itemId: this.formData.itemId,
            initiatorName: this.formData.initiatorName,
            initiatorPhone: this.formData.initiatorPhone,
            bookingTime: bookingTime,
            problemDesc: this.formData.problemDesc,
            problemImgs: this.imageList.map(img => img.url || img).join(',')
          }

          if (this.formData.orderType === 1) {
            params.addressDetail = this.formData.addressDetail
            if (this.formData.buildingId) params.buildingId = this.formData.buildingId
          } else {
            params.roomId = this.formData.roomId
            params.buildingId = this.formData.buildingId
            params.unitId = this.formData.unitId
            const building = this.buildings.find(b => b.id === this.formData.buildingId)
            const unit = this.units.find(u => u.id === this.formData.unitId)
            const room = this.rooms.find(r => r.roomId === this.formData.roomId)
            let fullRoomName = ''
            if (building) fullRoomName += building.floorNum
            if (unit) fullRoomName += unit.unitNum
            if (room) fullRoomName += room.roomNum
            params.roomName = fullRoomName || (room ? room.roomNum : '')
          }

          await saveOrUpdateRepairOrder(params)
          this.$emit('success')
        } catch (error) {
          console.log('修改工单失败:', error)
          this.$message.error(error.message || '修改失败')
        } finally {
          this.submitting = false
        }
      })
    }
  }
}
</script>

<style scoped>
.edit-repair-order {
  padding: 20px;
}

/* 表单标签左对齐 */
.edit-repair-order ::v-deep .el-form-item__label {
  text-align: left;
}

.edit-repair-order ::v-deep .el-form-item__content {
  text-align: left;
}

.edit-repair-order ::v-deep .el-radio-group {
  display: flex;
  justify-content: flex-start;
  align-items: center;
}

.profession-name {
  color: #666;
  font-size: 14px;
}

/* 报修分类选择弹窗样式 */
.category-content {
  max-height: 500px;
  overflow-y: auto;
}

.empty-category {
  text-align: center;
  padding: 60px 0;
  color: #999;
  font-size: 14px;
}

.category-sections {
  padding: 0;
}

.category-section {
  margin-bottom: 24px;
}

.category-section:last-child {
  margin-bottom: 0;
}

.category-name {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 12px;
  padding-left: 12px;
  position: relative;
}

.category-name::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 4px;
  height: 16px;
  background: #409EFF;
  border-radius: 2px;
}

.tag-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;
}

.category-section:last-child .tag-grid {
  border-bottom: none;
}

.tag-item {
  padding: 8px 16px;
  background: #f5f5f5;
  border-radius: 4px;
  font-size: 14px;
  color: #666;
  cursor: pointer;
  transition: all 0.3s;
  border: 1px solid transparent;
}

.tag-item:hover {
  background: #e6f2ff;
  color: #409EFF;
}

.tag-item.tag-active {
  background: #e6f2ff;
  color: #409EFF;
  border-color: #409EFF;
}

.form-footer {
  text-align: right;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #eee;
}

.upload-tip {
  font-size: 12px;
  color: #999;
  margin-top: 5px;
}
</style>
